import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi, financeApi } from './endpoints';

export function useInventoryItems(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['inventory', params],
    queryFn: () => inventoryApi.listItems(params),
  });
}

export function useInventoryItem(id: string) {
  return useQuery({
    queryKey: ['inventory', id],
    queryFn: () => inventoryApi.getItem(id),
    enabled: !!id,
  });
}

export function useLowStockItems() {
  return useQuery({
    queryKey: ['inventory', 'low-stock'],
    queryFn: () => inventoryApi.getLowStock(),
  });
}

export function useCreateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => inventoryApi.createItem(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useUpdateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => inventoryApi.updateItem(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useCreateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) =>
      inventoryApi.createMovement(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useInventoryCategories() {
  return useQuery({
    queryKey: ['inventory', 'categories'],
    queryFn: () => inventoryApi.listCategories(),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => inventoryApi.createCategory(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'categories'] });
    },
  });
}

export function useInventoryLocations() {
  return useQuery({
    queryKey: ['inventory', 'locations'],
    queryFn: () => inventoryApi.listLocations(),
  });
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => inventoryApi.createLocation(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inventory', 'locations'] });
    },
  });
}

export function useFinanceEntries(params?: Record<string, unknown>) {
  return useQuery({ queryKey: ['finance', params], queryFn: () => financeApi.listEntries(params) });
}

export function useFinanceEntry(id: string) {
  return useQuery({
    queryKey: ['finance', id],
    queryFn: () => financeApi.getEntry(id),
    enabled: !!id,
  });
}

export function useFinanceSummary() {
  return useQuery({ queryKey: ['finance', 'summary'], queryFn: () => financeApi.getSummary() });
}

export function useCreateFinanceEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => financeApi.createEntry(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance'] });
    },
  });
}

export function useUpdateFinanceEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: unknown }) => financeApi.updateEntry(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance'] });
    },
  });
}

export function useChangeFinanceStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      financeApi.changeStatus(id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance'] });
    },
  });
}

export function useCancelFinanceEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => financeApi.cancelEntry(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance'] });
    },
  });
}

export function useFinanceCategories(type?: string) {
  return useQuery({
    queryKey: ['finance', 'categories', type],
    queryFn: () => financeApi.listCategories(),
  });
}

export function useCreateFinanceCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, type }: { name: string; type: string }) =>
      financeApi.createCategory(name, type),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance', 'categories'] });
    },
  });
}
